import { useRef, useState } from 'react';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import Modal from 'components/Common/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import AnnouncementGroupAutocomplete from 'components/Announcemnet/AnnouncementGroupAutocomplete';
import { useForm } from 'react-hook-form';
import CardWrapper from 'components/Common/CardWrapper';
import { ANNOUNCEMENTS, NewAnnouncement, SendAnnouncementProps } from 'types/announcements';
import { sendAnnouncement } from 'redux/slices/announcements/sendAnnouncementSlice';
import Routes from 'constants/routes';
import { setAnnouncementsTab } from 'redux/slices/common/appSlice';
import MuiLoader from 'components/Common/Loader';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';

const ANNOUNCEMENT_TITLE = 'Make An Announcement';
const ANNOUNCEMENT_DESCRIPTION =
    'Fill in the appropriate fields and send a notification out to your community. Once a notification has been sent it cannot be undone. Make sure your information is correct before sending.';

function newIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `ann-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export default function SendAnnouncement({ defaultValues, resendBlocked }: SendAnnouncementProps) {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const [modalOpen, setModalOpen] = useState(false);
    const [announcementSentModal, setAnnouncementSentModal] = useState(false);
    /** One key per user send/resend action; reused across retries until success (H1). */
    const idempotencyKeyRef = useRef<string | null>(null);
    const { loading, announcement } = useSelector((state: RootState) => ({
        loading: state.announcement.sendAnnouncement.loading,
        announcement: state.announcement.announcementDetails,
    }));
    const { loading: getAnnouncementLoading } = announcement || {};
    const groupId = defaultValues?.groupId;

    const { setValue, watch, getValues, reset } = useForm<NewAnnouncement>({ defaultValues });
    const title = watch('title');
    const message = watch('message');
    const watchedGroupId = watch('groupId');
    const isAnnouncementIncomplete = (values: Partial<NewAnnouncement>) =>
        !values?.groupId || !values?.title?.trim() || !values?.message?.trim();
    const isFormIncomplete = isAnnouncementIncomplete({ groupId: watchedGroupId, title, message });

    const handleConfirm = async () => {
        if (resendBlocked) return;
        if (!parishId || !canManageAnnouncements) return;
        const formValues = getValues();
        if (isAnnouncementIncomplete(formValues)) {
            return;
        }
        if (!idempotencyKeyRef.current) {
            idempotencyKeyRef.current = newIdempotencyKey();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing dispatched thunk payload union
        const { payload }: any = await dispatch(
            sendAnnouncement({
                parishId,
                announcement: formValues,
                idempotencyKey: idempotencyKeyRef.current,
            })
        );
        if (payload?.ok) {
            idempotencyKeyRef.current = null;
            setAnnouncementSentModal(true);
            setModalOpen(false);
            reset();
        }
        // On failure: keep the same key so the next confirm retries idempotently.
    };

    const goToAnnouncements = () => {
        dispatch(setAnnouncementsTab(ANNOUNCEMENTS));
        navigate(Routes.announcements.main);
    };

    if (!canManageAnnouncements) {
        return (
            <CardWrapper stackProps={{ alignItems: 'center' }}>
                <Alert severity="error">You need Merchant Admin or higher to send an announcement.</Alert>
            </CardWrapper>
        );
    }

    return (
        <CardWrapper stackProps={{ alignItems: 'center' }}>
            <MuiLoader absolute loading={getAnnouncementLoading} />
            <Stack maxWidth={1016}>
                <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                    {ANNOUNCEMENT_TITLE}
                </Typography>
                <Divider />
                <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                    {ANNOUNCEMENT_DESCRIPTION}
                </Typography>
                <Stack mx={{ md: 8 }}>
                    <AnnouncementGroupAutocomplete
                        defaultValue={groupId ? String(groupId) : undefined}
                        setGroup={(group) => setValue('groupId', group?.id ?? null)}
                    />
                    <TextInput
                        value={title}
                        onChange={({ target }) => setValue('title', target.value)}
                        label="Title"
                        icon={null}
                        width="100%"
                        placeholder="Type announcement title"
                        sx={{ mt: 2 }}
                    />
                    <TextInput
                        value={message}
                        onChange={({ target }) => setValue('message', target.value)}
                        label="Message"
                        icon={null}
                        multiline
                        minRows={6}
                        maxRows={6}
                        width="100%"
                        placeholder="Type announcement message here"
                        sx={{ mt: 2, mb: 18, p: 0 }}
                    />
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        width={372}
                        disabled={resendBlocked || isFormIncomplete}
                        onClick={() => {
                            if (resendBlocked || isFormIncomplete) return;
                            setModalOpen(true);
                        }}
                    >
                        {id ? 'Resend' : 'Send'}
                    </Button>
                </Box>

                <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle={`Confirm ${id ? 'Resend' : 'Send'}`}>
                    <>
                        <Typography sx={{ fontSize: 18, my: 2 }}>
                            {`Are you sure you want to ${id ? 'resend' : 'send'} this announcement?`}
                        </Typography>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                                marginTop: 32,
                            }}
                        >
                            <Button backgroundColor="white" onClick={() => setModalOpen(false)} width={'45%'} title="No" />
                            <Button loading={loading} onClick={handleConfirm} width={'45%'} title="Yes" />
                        </div>
                    </>
                </Modal>

                <Modal
                    isOpen={announcementSentModal}
                    onClose={() => setAnnouncementSentModal(false)}
                    modalTitle={`Announcement ${id ? 'Resent' : 'Sent'}!`}
                >
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        Your announcement has been sent to the group selected, you may view it in the Announcements section of this portal.{' '}
                        <br />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                                marginTop: 32,
                            }}
                        >
                            <Button onClick={goToAnnouncements} width={'80%'} title="Go to Announcements" />
                        </div>
                    </Typography>
                </Modal>

                <AssistanceFooter />
            </Stack>
        </CardWrapper>
    );
}
